/* ============================================================
   bundle.js — Web Component <bundle-section>
   Drives the dynamic state of sections/bundle.liquid:
     - Pending quantity per card (data-bundle-pending-qty)
     - Selected items list -> carousel slots (one per unit)
     - Progress bar fill + tier markers (Starter/Standard/Most
       Popular/Max Savings), driven by data-bundle-discount-rules
     - Sidebar stats (packs / unit price / total) with tier discount
     - Add-to-cart POST to /cart/add.js with _bb_* line properties

   No build step required — vanilla custom element, loaded by
   sections/bundle.liquid via <script src="…" defer>.
   ============================================================ */

class BundleSection extends HTMLElement {
  // Fallback segment count for progress normalisation. The effective value
  // is computed at render-time as max(this fallback, highest tier minimum).
  // This keeps legacy bundles working while allowing higher tier configs
  // (e.g. 20 cans) to render the last marker and fill correctly.
  static PROGRESS_SEGMENT_COUNT = 10;

  // How far along its 1/N segment each non-last marker sits (0 = segment start,
  // 1 = segment end). Lower = markers shift LEFT (smaller start→first gap, wider
  // gap before the final marker); higher = they shift RIGHT. Kept below 1 so the
  // penultimate tag ("Most Popular") still clears the right-aligned last tag
  // ("Max Savings") without overlapping. Mobile-only (see _isMobileProgress).
  static PROGRESS_MARKER_LEAD = 0.6;

  // Flat shipping cost ($8) shown in the sticky CTA price while the active
  // plan hasn't unlocked free shipping yet. DISPLAY-ONLY: it is added to the
  // shown total to nudge toward the threshold, but never to pricing.finalCents
  // or the /cart/add payload. Drops to 0 once free shipping is reached.
  static SHIPPING_SURCHARGE_CENTS = 800;

  // Fallbacks for the client-editable strings that live in the THEME EDITOR
  // (passed in as JSON via data-bundle-texts). The status/progress badge text
  // is NOT here — it comes per-tier from the discount_tier metaobject (message
  // / message_last fields). A tier with no message is skipped; the badge shows
  // the next pending tier's message instead (see _computeUnlockMessage).
  /** @type {Record<string, string>} */
  static DEFAULT_TEXTS = {
    progress_max: 'MAXIMUM SAVINGS',
    tier_cans: '{count} cans',
    tier_save: 'save {percent}%',
    savings_format: '-{amount} ({percent}% off)',
    save_badge: 'SAVE {amount}',
    free_shipping_marker: 'Free Shipping',
    tier_free_shipping: '+ Free Shipping',
  };

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    // ── Config from data attributes ──
    this._maxQty = parseInt(this.dataset.bundleMaxQty || '5', 10) || 5;
    this._minQty = parseInt(this.dataset.bundleMinQty || '1', 10) || 1;
    if (this._minQty > this._maxQty) this._minQty = this._maxQty;

    try {
      this._discountRules = JSON.parse(this.dataset.bundleDiscountRules || '[]') || [];
    } catch (err) {
      this._discountRules = [];
    }
    this._discountRules.sort((a, b) => (a.minimum || 0) - (b.minimum || 0));

    // Theme-editor wording for the tier chips, savings rows and max-savings
    // state (data-bundle-texts). Falls back to {} → DEFAULT_TEXTS if absent.
    try {
      this._texts = JSON.parse(this.dataset.bundleTexts || '{}') || {};
    } catch (err) {
      this._texts = {};
    }

    this._parentVariantId = this.dataset.parentVariantId || '';
    this._parentTitle = this.dataset.parentTitle || '';
    this._parentImage = this.dataset.parentImage || '';
    this._parentPriceCents = parseInt(this.dataset.parentPrice || '0', 10) || 0;
    const configuredDiscountPct = parseInt(this.dataset.subscriptionDiscountPct || '10', 10);
    this._subscriptionDiscountPct = Number.isNaN(configuredDiscountPct)
      ? 10
      : Math.max(0, configuredDiscountPct);
    this._moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    // ── Element references ──
    // Status/progress hooks are collections — the same block is mirrored in
    // the sticky cart bar, so both copies must update together. The fill lives
    // inside each [data-bundle-progress], so it's queried per-progress below.
    this._countEls = this.querySelectorAll('[data-bundle-selected-count]');
    this._unlockMessageEls = this.querySelectorAll('[data-bundle-unlock-message]');
    // Wrapper pill (dot + message) — hidden when there's no message to show, so
    // we never render an empty pill with just the dot.
    this._unlockTagEls = this.querySelectorAll('[data-bundle-unlock-tag]');
    this._progressEls = this.querySelectorAll('[data-bundle-progress]');
    // Sticky "PACKS SELECTED (N)" summary count — rendered as `(N)`.
    this._stickyPacksCountEls = this.querySelectorAll('[data-bundle-sticky-packs-count]');
    this._carouselEl = this.querySelector('[data-bundle-carousel]');
    this._swiperEl = this.querySelector('[data-bundle-swiper]');
    this._trackEl = this.querySelector('[data-bundle-track]');
    this._packsCountEl = this.querySelector('[data-bundle-packs-count]');
    this._unitPriceEl = this.querySelector('[data-bundle-unit-price]');
    this._totalEl = this.querySelector('[data-bundle-total]');
    this._totalOriginalEl = this.querySelector('[data-bundle-total-original]');
    this._tierSavingsEl = this.querySelector('[data-bundle-tier-savings]');
    this._subscriptionSavingsEl = this.querySelector('[data-bundle-subscription-savings]');
    this._subscribeBadgeEl = this.querySelector('[data-bundle-subscribe-badge]');
    this._stickySubscribeLabelEl = this.querySelector('[data-bundle-sticky-subscribe-label]');
    this._saveBadgeEl = this.querySelector('[data-bundle-save-badge]');
    this._subscribeToggleEl = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-subscribe-toggle]'));
    this._addToCartBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-add-to-cart]'));

    this._slotTemplate = this.querySelector('[data-bundle-slot-template]');
    this._slotEmptyTemplate = this.querySelector('[data-bundle-slot-empty-template]');
    this._markerTemplate = this.querySelector('[data-bundle-progress-marker-template]');
    // Discount tier chips — one per visible rule, mirrored from the progress
    // markers so they stay 1:1 with the bar. querySelectorAll so a future
    // sticky-cart copy would update automatically.
    this._tierTemplate = this.querySelector('[data-bundle-tier-template]');
    this._tierContainerEls = this.querySelectorAll('[data-bundle-tiers]');

    this._prevBtn = this.querySelector('[data-bundle-prev]');
    this._nextBtn = this.querySelector('[data-bundle-next]');

    // ── Sticky cart references (markup lives inside <bundle-section>) ──
    // The sticky bar is purely presentational — BundleSection writes into
    // its slots and CTA so we keep a single source of truth for state.
    this._sticky = {
      el: this.querySelector('bundle-sticky-cart'),
      trackEl: this.querySelector('[data-bundle-sticky-track]'),
      swiperEl: this.querySelector('[data-bundle-sticky-swiper]'),
      prevBtn: this.querySelector('[data-bundle-sticky-prev]'),
      nextBtn: this.querySelector('[data-bundle-sticky-next]'),
      slotTemplate: /** @type {HTMLTemplateElement | null} */ (this.querySelector('[data-bundle-sticky-slot-template]')),
      slotEmptyTemplate: /** @type {HTMLTemplateElement | null} */ (this.querySelector('[data-bundle-sticky-slot-empty-template]')),
      subscribeToggle: /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-sticky-subscribe-toggle]')),
      deliveryTrigger: /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-sticky-delivery-trigger]')),
      deliveryTextEl: this.querySelector('[data-bundle-sticky-delivery-text]'),
      deliveryOptions: this.querySelector('[data-bundle-sticky-delivery-options]'),
      addToCartBtn: /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-sticky-add-to-cart]')),
      // CTA inner parts — the sticky button is split into a label span
      // (left) and a pricing wrapper (right) so we can update prices
      // without rewriting the button's textContent (which would also
      // wipe the structure during ADDING…/✓ ADDED transient states).
      ctaLabelEl: this.querySelector('[data-bundle-sticky-cta-label]'),
      ctaPricingEl: this.querySelector('[data-bundle-sticky-cta-pricing]'),
      ctaOriginalEl: this.querySelector('[data-bundle-sticky-cta-original]'),
      ctaFinalEl: this.querySelector('[data-bundle-sticky-cta-final]'),
      // Free-shipping pill — wrapper (toggled hidden) + the "+N" count span +
      // the trailing label ("to free shipping" → "Free Shipping" once met).
      freeshipEl: /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-sticky-freeship]')),
      freeshipCountEl: this.querySelector('[data-bundle-sticky-freeship-count]'),
      freeshipLabelEl: this.querySelector('[data-bundle-sticky-freeship-label]'),
      swiper: /** @type {any} */ (null),
    };

    // ── State ──
    this._pendingByCard = new WeakMap();
    /** @type {Array<{ variantId: string, price: number, title: string, image: string, handle: string, bgColor: string }>} */
    this._selected = [];
    this._submitting = false;
    this._swiper = null;
    // Tracks whether the free-shipping threshold was reached on the previous
    // render so the celebration fires only on the false→true crossing — not
    // on every render while it stays reached. `null` means "not yet measured"
    // so the very first render (items restored from storage) never animates.
    this._fsReachedPrev = null;

    // Progress marker spacing + fill differ by viewport: mobile gets the
    // evenly-spaced markers (set inline by JS), desktop keeps the original
    // minimum-based layout. A CSS media query can't switch inline values, so we
    // track the breakpoint here (same 768px the stylesheet uses) and re-render
    // the bar whenever it's crossed. Set up before the first _renderAll so the
    // initial render already picks the right layout.
    this._progressMedia = (typeof window !== 'undefined' && window.matchMedia)
      ? window.matchMedia('(max-width: 768px)')
      : null;
    if (this._progressMedia) {
      this._progressMediaHandler = () => this._renderProgress();
      this._progressMedia.addEventListener('change', this._progressMediaHandler);
    }

    this._bindEvents();
    this._renderAll();
    this._initSwiper();
    this._initDeliverySelect();
    this._initStickyDeliverySelect();
  }

  disconnectedCallback() {
    if (this._swiper && typeof this._swiper.destroy === 'function') {
      this._swiper.destroy(true, true);
      this._swiper = null;
    }
    if (this._sticky && this._sticky.swiper && typeof this._sticky.swiper.destroy === 'function') {
      this._sticky.swiper.destroy(true, true);
      this._sticky.swiper = null;
    }
    if (this._deliveryOutsideHandler) {
      document.removeEventListener('click', this._deliveryOutsideHandler);
      this._deliveryOutsideHandler = null;
    }
    if (this._progressMedia && this._progressMediaHandler) {
      this._progressMedia.removeEventListener('change', this._progressMediaHandler);
      this._progressMediaHandler = null;
    }
  }

  // ── Swiper carousel ───────────────────────────────────────
  // Per SMG standard: sliders use the Swiper library (loaded globally
  // from theme.liquid) inside a Web Component. slidesPerView matches
  // bundle_max_qty so the viewport keeps the same width as the initial
  // empty grid; extras overflow and are reached via the prev/next
  // navigation buttons that Swiper wires automatically.
  _initSwiper() {
    if (typeof window === 'undefined' || typeof window.Swiper !== 'function') return;

    if (this._swiperEl) {
      const visibleSlides = Math.max(1, this._maxQty);
      this._swiper = new window.Swiper(this._swiperEl, {
        slidesPerView: visibleSlides,
        spaceBetween: 8,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation: {
          prevEl: this._prevBtn || undefined,
          nextEl: this._nextBtn || undefined,
          disabledClass: 'swiper-button-disabled',
        },
        breakpoints: {
          // Mobile: shrink to 2.5 visible slides like the rest of the theme.
          320: { slidesPerView: 4, spaceBetween: 0 },
          769: { slidesPerView: visibleSlides, spaceBetween: 8 },
        },
      });
    }

    // Sticky bar carousel — matches Figma node 818:16776: 60px circular
    // slots, gap 8px. Using slidesPerView: 'auto' so each slot keeps its
    // CSS width (60px clamp) and Swiper just shows as many as the slots
    // area can fit — arrows handle the overflow. watchOverflow hides the
    // navigation when everything already fits without scrolling.
    if (this._sticky && this._sticky.swiperEl) {
      this._sticky.swiper = new window.Swiper(this._sticky.swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation: {
          prevEl: this._sticky.prevBtn || undefined,
          nextEl: this._sticky.nextBtn || undefined,
          disabledClass: 'swiper-button-disabled',
        },
      });
    }
  }

  // ── Delivery frequency select ─────────────────────────────
  // Mirrors product-spotlight._initDeliverySelect: a <button> trigger
  // toggles an absolute <ul> listbox, options update the visible label
  // and the trigger's data-bundle-selected-selling-plan-id. The plan id
  // is included as a per-item property in _addBundleToCart so the cart
  // line registers the subscription.
  _initDeliverySelect() {
    const select = this.querySelector('[data-bundle-delivery-select]');
    if (!select) return;

    const trigger = select.querySelector('[data-bundle-delivery-trigger]');
    const list = select.querySelector('[data-bundle-delivery-options]');
    const label = select.querySelector('[data-bundle-delivery-text]');
    if (!trigger || !list || !label) return;

    this._deliverySelectEl = select;
    this._deliveryTriggerEl = trigger;

    const open = () => {
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      select.classList.add('bundle-sidebar__delivery-select--open');
    };

    const close = () => {
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      select.classList.remove('bundle-sidebar__delivery-select--open');
    };

    list.hidden = true;

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      if (trigger.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    list.addEventListener('click', (event) => {
      const option = event.target && event.target.closest
        ? event.target.closest('.bundle-sidebar__delivery-option')
        : null;
      if (!option) return;

      list.querySelectorAll('.bundle-sidebar__delivery-option').forEach((o) => {
        o.classList.remove('bundle-sidebar__delivery-option--selected');
        o.setAttribute('aria-selected', 'false');
      });

      option.classList.add('bundle-sidebar__delivery-option--selected');
      option.setAttribute('aria-selected', 'true');
      const labelText = option.textContent ? option.textContent.trim() : '';
      label.textContent = labelText;

      const selectedPlanId = option.dataset.sellingPlanId || '';
      trigger.dataset.bundleSelectedSellingPlanId = selectedPlanId;

      // Mirror the choice to the sticky bar so both sources of truth match
      // when the cart submission reads the selling plan id.
      this._syncStickyDelivery(selectedPlanId, labelText);

      close();
    });

    this._deliveryOutsideHandler = (event) => {
      if (!select.contains(event.target)) close();
    };
    document.addEventListener('click', this._deliveryOutsideHandler);
  }

  // ── Sticky delivery select ────────────────────────────────
  // Same structure as _initDeliverySelect but scoped to the sticky bar's
  // markup. Selecting an option here updates the sticky trigger AND the
  // main sidebar so _addBundleToCart picks up the right selling plan id.
  // The dropdown open/close is handled in bundle-sticky-cart.js — we only
  // wire the option-click behaviour here because it has to mutate state
  // owned by BundleSection.
  _initStickyDeliverySelect() {
    const sticky = this._sticky;
    if (!sticky) return;
    const list = sticky.deliveryOptions;
    const trigger = sticky.deliveryTrigger;
    const labelEl = sticky.deliveryTextEl;
    if (!list || !trigger || !labelEl) return;

    list.addEventListener('click', (event) => {
      const target = /** @type {Element | null} */ (event.target);
      const option = /** @type {HTMLElement | null} */ (target && target.closest ? target.closest('.bundle-sticky-cart__delivery-option') : null);
      if (!option) return;

      list.querySelectorAll('.bundle-sticky-cart__delivery-option').forEach((o) => {
        o.classList.remove('bundle-sticky-cart__delivery-option--selected');
        o.setAttribute('aria-selected', 'false');
      });

      option.classList.add('bundle-sticky-cart__delivery-option--selected');
      option.setAttribute('aria-selected', 'true');
      const labelText = option.textContent ? option.textContent.trim() : '';
      labelEl.textContent = labelText;

      const selectedPlanId = option.dataset.sellingPlanId || '';
      trigger.dataset.bundleSelectedSellingPlanId = selectedPlanId;

      // Mirror to the main sidebar — _addBundleToCart reads the plan id
      // from this._deliveryTriggerEl.dataset.bundleSelectedSellingPlanId,
      // so keeping both in sync means either bar can submit identically.
      this._syncMainDelivery(selectedPlanId, labelText);

      // Close the sticky dropdown — bundle-sticky-cart.js exposes no API,
      // so we close it via the same DOM contract its open()/close() use.
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      const select = trigger.closest('[data-bundle-sticky-delivery-select]');
      if (select) select.classList.remove('bundle-sticky-cart__delivery-select--open');
    });
  }

  /**
   * @param {string} planId
   * @param {string} labelText
   */
  _syncStickyDelivery(planId, labelText) {
    const sticky = this._sticky;
    if (!sticky) return;
    if (sticky.deliveryTrigger) {
      sticky.deliveryTrigger.dataset.bundleSelectedSellingPlanId = planId;
    }
    if (sticky.deliveryTextEl && labelText) {
      sticky.deliveryTextEl.textContent = labelText;
    }
    if (sticky.deliveryOptions) {
      sticky.deliveryOptions.querySelectorAll('.bundle-sticky-cart__delivery-option').forEach((/** @type {Element} */ o) => {
        const el = /** @type {HTMLElement} */ (o);
        const matches = (el.dataset.sellingPlanId || '') === planId;
        el.classList.toggle('bundle-sticky-cart__delivery-option--selected', matches);
        el.setAttribute('aria-selected', matches ? 'true' : 'false');
      });
    }
  }

  /**
   * @param {string} planId
   * @param {string} labelText
   */
  _syncMainDelivery(planId, labelText) {
    if (this._deliveryTriggerEl) {
      const trigger = /** @type {HTMLElement} */ (this._deliveryTriggerEl);
      trigger.dataset.bundleSelectedSellingPlanId = planId;
    }
    const labelEl = this.querySelector('[data-bundle-delivery-text]');
    if (labelEl && labelText) labelEl.textContent = labelText;
    const list = this.querySelector('[data-bundle-delivery-options]');
    if (list) {
      list.querySelectorAll('.bundle-sidebar__delivery-option').forEach((/** @type {Element} */ o) => {
        const el = /** @type {HTMLElement} */ (o);
        const matches = (el.dataset.sellingPlanId || '') === planId;
        el.classList.toggle('bundle-sidebar__delivery-option--selected', matches);
        el.setAttribute('aria-selected', matches ? 'true' : 'false');
      });
    }
  }

  // ── Event wiring (delegated) ──────────────────────────────
  _bindEvents() {
    this.addEventListener('click', (event) => {
      const minusBtn = event.target.closest('[data-bundle-pending-minus]');
      if (minusBtn && this.contains(minusBtn)) {
        const card = minusBtn.closest('[data-bundle-card]');
        if (card) this._adjustPending(card, -1);
        return;
      }

      const plusBtn = event.target.closest('[data-bundle-pending-plus]');
      if (plusBtn && this.contains(plusBtn)) {
        const card = plusBtn.closest('[data-bundle-card]');
        if (card) this._adjustPending(card, +1);
        return;
      }

      const addBtn = event.target.closest('[data-bundle-add-to-bundle]');
      if (addBtn && this.contains(addBtn)) {
        const card = addBtn.closest('[data-bundle-card]');
        if (card) this._commitPending(card);
        return;
      }

      // ── Grouped tile controls (main + sticky) ──────────────
      // Each filled tile is a variant group. The stepper +/- add/remove one
      // unit of that variant; the × removes the whole group. Variant id +
      // payload live on the slot's data-attrs (see _renderCarouselInto), so
      // the same logic serves both carousels — only the hook strings differ.
      const qtyMinusBtn = event.target.closest('[data-bundle-slot-qty-minus], [data-bundle-sticky-qty-minus]');
      if (qtyMinusBtn && this.contains(qtyMinusBtn)) {
        const slot = qtyMinusBtn.closest('[data-bundle-slot], [data-bundle-sticky-slot]');
        if (slot && slot.dataset.variantId) this._removeOneByVariant(slot.dataset.variantId);
        return;
      }

      const qtyPlusBtn = event.target.closest('[data-bundle-slot-qty-plus], [data-bundle-sticky-qty-plus]');
      if (qtyPlusBtn && this.contains(qtyPlusBtn)) {
        const slot = qtyPlusBtn.closest('[data-bundle-slot], [data-bundle-sticky-slot]');
        if (slot && slot.dataset.variantId) this._addOneByVariant(this._payloadFromSlot(slot));
        return;
      }

      // Remove buttons (main + sticky) drop ALL units of the tile's variant.
      // Slots are rendered inside <bundle-sticky-cart> which lives inside
      // <bundle-section>, so this single delegated listener catches both.
      const removeBtn = event.target.closest('[data-bundle-slot-remove], [data-bundle-sticky-slot-remove]');
      if (removeBtn && this.contains(removeBtn)) {
        const slot = removeBtn.closest('[data-bundle-slot], [data-bundle-sticky-slot]');
        if (slot && slot.dataset.variantId) this._removeAllByVariant(slot.dataset.variantId);
        return;
      }

      const cartBtn = event.target.closest('[data-bundle-add-to-cart]');
      if (cartBtn && this.contains(cartBtn)) {
        this._addBundleToCart();
        return;
      }

      // Sticky bar — Add to cart. Same handler as the main CTA; the
      // _addBundleToCart method writes feedback to both buttons.
      const stickyCartBtn = event.target.closest('[data-bundle-sticky-add-to-cart]');
      if (stickyCartBtn && this.contains(stickyCartBtn)) {
        this._addBundleToCart();
        return;
      }
      // Carousel prev/next are bound by Swiper via navigation options.
    });

    // Subscribe & Save toggle (both sides). Either side flipping recomputes
    // the sidebar pricing and mirrors the checked state to the twin. Local
    // refs avoid TS narrowing loss inside the change closures and keep the
    // sync one-way per event so we don't loop.
    const mainToggle = this._subscribeToggleEl;
    const stickyToggle = this._sticky ? this._sticky.subscribeToggle : null;

    if (mainToggle) {
      mainToggle.addEventListener('change', () => {
        if (stickyToggle) stickyToggle.checked = mainToggle.checked;
        this._renderAll();
      });
    }
    if (stickyToggle) {
      stickyToggle.addEventListener('change', () => {
        if (mainToggle) mainToggle.checked = stickyToggle.checked;
        this._renderAll();
      });
    }
  }

  // ── Pending qty handlers ──────────────────────────────────
  // Selection is uncapped — users can keep adding past maxQty (Max Savings
  // and beyond). maxQty is only a hint for how many empty slots to pad.
  _getPending(cardEl) {
    return this._pendingByCard.get(cardEl) || 1;
  }

  _adjustPending(cardEl, delta) {
    const current = this._getPending(cardEl);
    const next = Math.max(1, current + delta);
    this._pendingByCard.set(cardEl, next);
    this._renderCardPending(cardEl);
  }

  _renderCardPending(cardEl) {
    const value = this._getPending(cardEl);
    const display = cardEl.querySelector('[data-bundle-pending-qty]');
    if (display) display.textContent = value;

    const minusBtn = cardEl.querySelector('[data-bundle-pending-minus]');
    if (minusBtn) minusBtn.disabled = value <= 1;
  }

  _refreshAllCardSteppers() {
    const cards = this.querySelectorAll('[data-bundle-card]');
    cards.forEach((card) => {
      if (!this._pendingByCard.has(card)) this._pendingByCard.set(card, 1);
      this._renderCardPending(card);
    });
  }

  // ── Commit pending → selected list ────────────────────────
  _commitPending(cardEl) {
    const pending = this._getPending(cardEl);
    if (pending <= 0) return;

    const variantId = cardEl.dataset.bundleCardVariantId || '';
    const priceCents = parseInt(cardEl.dataset.bundleCardVariantPrice || '0', 10) || 0;
    const title = cardEl.dataset.bundleCardTitle || '';
    const image = cardEl.dataset.bundleCardImage || '';
    const handle = cardEl.dataset.bundleCardHandle || '';
    const bgColor = cardEl.dataset.bundleCardBgColor || '';

    if (!variantId) return;

    for (let i = 0; i < pending; i++) {
      this._selected.push({ variantId, price: priceCents, title, image, handle, bgColor });
    }

    this._pendingByCard.set(cardEl, 1);
    this._renderAll();
  }

  // ── Grouping (duplicates → one tile per variant) ──────────
  // The carousel + sticky bar collapse duplicate units into a single tile
  // showing the quantity via a stepper. `_selected` stays a FLAT array (one
  // entry per unit) so every count-driven feature (progress, tiers, pricing,
  // free-shipping, add-to-cart) keeps reading `.length` / iterating it
  // unchanged — only the rendering and per-tile controls group by variant.
  // Returns [{ item, count }] in first-appearance order.
  _groupSelected() {
    /** @type {Map<string, { item: any, count: number }>} */
    const byVariant = new Map();
    /** @type {Array<{ item: any, count: number }>} */
    const groups = [];
    this._selected.forEach((item) => {
      const id = item && item.variantId ? item.variantId : '';
      if (!id) return;
      let group = byVariant.get(id);
      if (!group) {
        group = { item, count: 0 };
        byVariant.set(id, group);
        groups.push(group);
      }
      group.count += 1;
    });
    return groups;
  }

  // ── Variant-keyed mutations (driven by the per-tile controls) ──
  // The stepper +/- add/remove one unit; the × button removes the whole
  // group. All three keep `_selected` flat and re-render everything so the
  // count-driven UI stays in sync.
  /** @param {{ variantId: string, price: number, title: string, image: string, handle: string, bgColor: string }} payload */
  _addOneByVariant(payload) {
    if (!payload || !payload.variantId) return;
    this._selected.push({ ...payload });
    this._renderAll();
  }

  /** @param {string} variantId */
  _removeOneByVariant(variantId) {
    if (!variantId) return;
    // Remove the LAST matching unit so earlier tiles stay positionally stable.
    for (let i = this._selected.length - 1; i >= 0; i--) {
      if (this._selected[i].variantId === variantId) {
        this._selected.splice(i, 1);
        break;
      }
    }
    this._renderAll();
  }

  /** @param {string} variantId */
  _removeAllByVariant(variantId) {
    if (!variantId) return;
    this._selected = this._selected.filter((item) => item.variantId !== variantId);
    this._renderAll();
  }

  // Reconstruct a selection payload from the data-attrs a grouped tile
  // carries, so the "+" control can push a fresh unit without re-querying
  // the source card. Mirrors the shape produced by _commitPending.
  /** @param {HTMLElement} slot */
  _payloadFromSlot(slot) {
    return {
      variantId: slot.dataset.variantId || '',
      price: parseInt(slot.dataset.price || '0', 10) || 0,
      title: slot.dataset.title || '',
      image: slot.dataset.image || '',
      handle: slot.dataset.handle || '',
      bgColor: slot.dataset.bgColor || '',
    };
  }

  // ── Master render ─────────────────────────────────────────
  _renderAll() {
    // Reflect selection state on the host element so CSS can gate the
    // savings rows, strikethrough total, SAVE pill, the stats-card
    // active styling and the SHIPPING row via attribute selectors.
    //   • has-items     → at least one selection
    //   • tier-reached  → an active discount rule applies
    //   • fs-reached    → total has crossed the first free-shipping rule
    const selectedCount = this._selected ? this._selected.length : 0;
    this.dataset.hasItems = selectedCount > 0 ? 'true' : 'false';
    const activeRule = this._findActiveRule(selectedCount);
    this.dataset.tierReached = activeRule ? 'true' : 'false';
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    const fsThreshold = this._getFsThreshold(subscribed);
    this.dataset.fsReached = fsThreshold !== null && selectedCount >= fsThreshold ? 'true' : 'false';
    this._renderStickyFreeShipping(selectedCount, fsThreshold);

    this._renderStatus();
    this._renderProgress();
    this._renderCarousel();
    this._renderSidebar();
    this._renderAddToCart();
    this._refreshAllCardSteppers();
  }

  // ── Free-shipping helper ──────────────────────────────────
  // Returns the `minimum` of the first rule with free_shipping: true,
  // or null when no rule grants free shipping. Shared by _renderAll
  // (to set data-fs-reached) and _renderStatus (to pick the message).
  _getFsThreshold(subscribed) {
    const rules = this._discountRules || [];
    const firstFsRule = rules.find((r) => {
      if (!(r.free_shipping || r.freeShipping) || (r.minimum || 0) <= 0) return false;
      const type = (r.free_shipping_type || r.freeShippingType || 'both').toLowerCase();
      return subscribed ? (type === 'subscription' || type === 'both') : (type === 'one-time' || type === 'both');
    });
    return firstFsRule ? firstFsRule.minimum : null;
  }

  // ── Shipping surcharge helper ─────────────────────────────
  // DISPLAY-ONLY flat shipping cost shown in the totals while the active plan
  // hasn't unlocked free shipping: SHIPPING_SURCHARGE_CENTS below the threshold,
  // 0 once met (and 0 when there are no items or no free-shipping rule). Shared
  // by the sidebar Total row and the sticky CTA so both add/drop it together.
  // Never folded into pricing.finalCents or the /cart/add payload.
  /** @param {ReturnType<BundleSection["_computeBundlePricing"]>} pricing */
  _shippingSurchargeCents(pricing) {
    if (!pricing || pricing.total <= 0) return 0;
    const fsThreshold = this._getFsThreshold(!!pricing.subscribed);
    if (fsThreshold === null || pricing.total >= fsThreshold) return 0;
    return BundleSection.SHIPPING_SURCHARGE_CENTS;
  }

  // ── Sticky free-shipping pill ─────────────────────────────
  // Two states, both keeping the orange pill visible:
  //   • below threshold → "+N to free shipping" (N = packs still needed)
  //   • threshold met   → "Free Shipping" (count hidden, pill stays)
  // fsThreshold already accounts for the subscribe toggle (6 subscription /
  // 8 one-time), so flipping the toggle — which re-runs _renderAll — updates
  // the message too. The pill is only hidden when no free-shipping rule
  // exists for the active plan.
  /** @param {number} selectedCount @param {number | null} fsThreshold */
  _renderStickyFreeShipping(selectedCount, fsThreshold) {
    const sticky = this._sticky;
    if (!sticky || !sticky.freeshipEl) return;
    if (fsThreshold === null) {
      sticky.freeshipEl.setAttribute('hidden', '');
      return;
    }
    const remaining = Math.max(0, fsThreshold - selectedCount);
    if (remaining > 0) {
      if (sticky.freeshipCountEl) {
        sticky.freeshipCountEl.textContent = `+${remaining}`;
        sticky.freeshipCountEl.removeAttribute('hidden');
      }
      if (sticky.freeshipLabelEl) sticky.freeshipLabelEl.textContent = 'to free shipping';
    } else {
      if (sticky.freeshipCountEl) {
        sticky.freeshipCountEl.textContent = '';
        sticky.freeshipCountEl.setAttribute('hidden', '');
      }
      if (sticky.freeshipLabelEl) sticky.freeshipLabelEl.textContent = 'Free Shipping';
    }
    sticky.freeshipEl.removeAttribute('hidden');
  }

  // ── Status (count + unlock message) ───────────────────────
  _renderStatus() {
    const total = this._selected.length;
    this._countEls.forEach((el) => { el.textContent = total; });
    this._stickyPacksCountEls.forEach((el) => { el.textContent = `(${total})`; });
    this._progressEls.forEach((el) => el.setAttribute('aria-valuenow', total));
    if (!this._unlockMessageEls.length) return;

    // Compute the message once, then mirror it into every copy of the block.
    const message = this._computeUnlockMessage(total);
    this._unlockMessageEls.forEach((el) => { el.textContent = message; });
    // Hide the whole pill (dot + text) when there's nothing to say, rather than
    // leaving an empty pill floating with just the dot.
    const hasMessage = typeof message === 'string' && message.trim().length > 0;
    this._unlockTagEls.forEach((el) => {
      if (hasMessage) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  // ── Editable text helpers ─────────────────────────────────
  // _txt returns the merchant string for a theme-editor key (from
  // data-bundle-texts), falling back to the built-in default. _fillText swaps
  // {token} placeholders (case-insensitive) for their runtime values, so both
  // {remaining} and {Remaining} — and {Minimum}, {Discount}, etc. — resolve.
  /** @param {string} key @returns {string} */
  _txt(key) {
    const value = this._texts ? this._texts[key] : undefined;
    if (typeof value === 'string' && value.length) return value;
    return BundleSection.DEFAULT_TEXTS[key] || '';
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number | undefined>} replacements
   * @returns {string}
   */
  _fillText(template, replacements) {
    let out = String(template || '');
    const map = replacements || {};
    Object.keys(map).forEach((token) => {
      out = out.replace(new RegExp('\\{' + token + '\\}', 'gi'), String(map[token] ?? ''));
    });
    return out;
  }

  // ── Plan visibility ───────────────────────────────────────
  // `free_shipping_type` gates which tiers show for the ACTIVE plan:
  //   • 'subscription' → only while the Subscribe & Save toggle is ON
  //   • 'one-time'     → only while it's OFF
  //   • 'both' (default / anything else) → always
  // This drives the chips, the progress markers AND the unlock message, so a
  // subscription-only tier disappears entirely when shopping one-time (and
  // vice-versa). Re-runs on every toggle change via _renderAll.
  /** @param {any} rule @param {boolean} subscribed @returns {boolean} */
  _isRuleVisibleForPlan(rule, subscribed) {
    const type = (rule.free_shipping_type || rule.freeShippingType || 'both').toLowerCase();
    if (type === 'subscription') return subscribed;
    if (type === 'one-time') return !subscribed;
    return true;
  }

  // ── Active-plan rules ─────────────────────────────────────
  // Tiers with a real quantity that are visible for the current plan (see
  // _isRuleVisibleForPlan). Order is preserved (already sorted by minimum).
  /** @returns {Array<any>} */
  _activePlanRules() {
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    return (this._discountRules || []).filter(/** @param {any} rule */ (rule) =>
      (rule.minimum || 0) > 0 && this._isRuleVisibleForPlan(rule, subscribed));
  }

  // ── Tier tokens ───────────────────────────────────────────
  // Values a per-tier message can reference via {token} (case-insensitive):
  // {remaining} (dynamic), {minimum}, {maximum}, {discount}/{value}, {label}.
  /** @param {any} rule @param {number} remaining */
  _tierTokens(rule, remaining) {
    const hasMax = !(rule.maximum === null || rule.maximum === undefined || rule.maximum === '');
    return {
      remaining: remaining,
      minimum: rule.minimum || 0,
      maximum: hasMax ? rule.maximum : '',
      discount: Math.round(Number(rule.value) || 0),
      value: Math.round(Number(rule.value) || 0),
      label: rule.label || '',
    };
  }

  // ── Unlock message ────────────────────────────────────────
  // Returns the status-tag (progress badge) text for `total` selected packs.
  // The wording is authored PER TIER in the discount_tier metaobject:
  //   • message              — shown while this tier is the next to unlock
  //   • message_last         — optional second message (see the flag below)
  //   • message_last_on_max  — where message_last shows:
  //       false (default) → one pack BEFORE reaching this tier
  //       true            → when this tier is REACHED (the terminal state);
  //                         lets the last step own its "MAXIMUM SAVINGS" text
  // Tokens ({remaining}/{minimum}/{maximum}/{discount}/{label}) pull from the
  // SAME tier. When a tier leaves the message fields blank, it's skipped and the
  // next pending tier's message is shown; if none of the pending tiers has one,
  // the badge is hidden. If the last tier doesn't provide a reached message,
  // the global theme-editor `progress_max` string is used.
  /** @param {number} total */
  _computeUnlockMessage(total) {
    // Plan-filtered so the message matches the visible progress bar/chips.
    const rules = this._activePlanRules();
    if (!rules.length) {
      return total === 0 ? '' : `SELECTED ${total}`;
    }

    // Tiers we're still working toward, in ascending order.
    const notReached = rules.filter(/** @param {any} r */ (r) => total < (r.minimum || 0));
    if (!notReached.length) {
      // Every tier reached. If the last tier opted to show its message_last on
      // the reached state, use it; otherwise the global max-savings string.
      const lastRule = rules[rules.length - 1];
      if (lastRule && lastRule.message_last_on_max
          && typeof lastRule.message_last === 'string' && lastRule.message_last.trim().length) {
        return this._fillText(lastRule.message_last, this._tierTokens(lastRule, 0));
      }
      return this._txt('progress_max');
    }

    // Walk the not-yet-reached tiers and show the FIRST one that has an authored
    // message. A tier with no message (nor an applicable message_last) is
    // skipped entirely — the badge borrows the next tier's message instead of
    // printing an auto-generated placeholder. If none of the pending tiers has
    // a message, return '' so _renderStatus hides the pill.
    for (let i = 0; i < notReached.length; i += 1) {
      const rule = notReached[i];
      const remaining = (rule.minimum || 0) - total;
      const tokens = this._tierTokens(rule, remaining);

      // message_last normally shows on the final pack (remaining === 1). But when
      // it's reserved for the reached state (message_last_on_max), keep showing
      // `message` here so it doesn't fire early.
      let template = rule.message;
      if (remaining === 1 && !rule.message_last_on_max
          && typeof rule.message_last === 'string' && rule.message_last.trim().length) {
        template = rule.message_last;
      }
      if (typeof template === 'string' && template.trim().length) {
        return this._fillText(template, tokens);
      }
      // No usable text on this tier → fall through to the next pending tier.
    }

    return '';
  }

  // ── Progress bar (fill + markers) ─────────────────────────
  // Matches bb-progress in bundle-builder.js:550-643:
  //   - PROGRESS_SEGMENT_COUNT (10) normalises fill, not maxQty.
  //   - .bundle__progress--min-reached when total >= first rule minimum.
  //   - .bundle__progress--free-shipping-reached when total >= first
  //     freeShipping rule minimum.
  //   - Markers get --reached when total >= rule.minimum.
  //   - The 3rd marker (Most Popular) and the last marker sit above the
  //     bar; the rest sit below.
  _renderProgress() {
    if (!this._progressEls.length) return;

    const total = this._selected.length;
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    // Only tiers visible for the active plan (subscribe vs one-time) — see
    // _isRuleVisibleForPlan. Everything below derives from this, so the bar
    // scale, first-tier glow, chips and markers all reflect the current plan.
    const rules = (this._discountRules || []).filter((rule) => this._isRuleVisibleForPlan(rule, subscribed));
    const highestTierMinimum = rules.reduce((max, rule) => {
      const minimum = Number(rule?.minimum || 0);
      return minimum > max ? minimum : max;
    }, 0);
    const segments = Math.max(BundleSection.PROGRESS_SEGMENT_COUNT, highestTierMinimum);

    const firstRule = rules.find((r) => Number.isInteger(r.minimum) && r.minimum > 0);
    const minimumQty = firstRule ? firstRule.minimum : 0;
    const fsRule = rules.find((r) => {
      if (!(r.free_shipping || r.freeShipping) || r.minimum <= 0) return false;
      const type = (r.free_shipping_type || r.freeShippingType || 'both').toLowerCase();
      return subscribed ? (type === 'subscription' || type === 'both') : (type === 'one-time' || type === 'both');
    });
    const freeShippingThreshold = fsRule ? fsRule.minimum : null;
    const freeShippingReached = freeShippingThreshold !== null && total >= freeShippingThreshold;

    // `rules` is already plan-filtered above, so this only drops zero/oversized
    // quantities. A missing label no longer hides the tier from the chips; on
    // the bar, captionless markers are skipped in the loop below (no bare dot).
    const visibleRules = rules.filter((rule) => {
      const minimum = rule.minimum || 0;
      return minimum > 0 && minimum <= segments;
    });

    // Mobile-only layout: evenly-spaced markers (set inline below) with a
    // piecewise-linear fill that still tracks the QUANTITY each tier needs —
    // landing exactly on a marker the instant its minimum is met. Desktop keeps
    // the original layout: markers positioned by minimum/segments and a plain
    // total/segments fill. See _computeProgressFill / _markerPositionPercent.
    const isMobile = this._isMobileProgress();
    let fillPercent;
    if (isMobile) {
      fillPercent = this._computeProgressFill(total, visibleRules, segments);
    } else {
      const filledCount = Math.min(Math.max(total, 0), segments);
      fillPercent = (filledCount / segments) * 100;
    }

    // Apply the same state to every copy of the block (main column + sticky).
    // The fill and markers live inside each [data-bundle-progress], so they're
    // scoped per-element rather than via shared refs.
    this._progressEls.forEach((progressEl) => {
      progressEl.classList.toggle('bundle__progress--min-reached', minimumQty > 0 && total >= minimumQty);
      progressEl.classList.toggle('bundle__progress--free-shipping-reached', freeShippingReached);

      const fillEl = progressEl.querySelector('[data-bundle-progress-fill]');
      if (fillEl) fillEl.style.width = fillPercent + '%';

      // Remove existing markers (templates are siblings, not inside; safe to clear by class)
      progressEl.querySelectorAll('.bundle__progress-marker').forEach((m) => m.remove());

      if (!this._markerTemplate) return;

      visibleRules.forEach((rule, idx) => {
        // A tier whose ONLY caption is "free shipping" wording (e.g. the
        // 6-pack tier configured for subscription-only free shipping) has
        // nothing meaningful to show at this position when it ISN'T the
        // active plan's free-shipping tier (fsRule) — e.g. the 6-pack while
        // shopping one-time, or the 8-pack while subscribed. Skip the whole
        // marker/dot rather than rendering an empty box.
        const rawLabel = rule.label || '';
        const isFreeShippingWording = /free\s*shipping/i.test(rawLabel);
        const isActiveFsRule = !!(fsRule && rule === fsRule);
        if (isFreeShippingWording && !isActiveFsRule) return;

        // Resolve the marker caption up front: the active free-shipping tier
        // shows the global "Free Shipping" wording, every other tier shows its
        // own label. A tier with no caption (no label, and not the active FS
        // tier) has nothing to show, so skip the WHOLE marker — no bare dot on
        // the bar.
        const markerCaption = (isActiveFsRule && !isFreeShippingWording)
          ? this._txt('free_shipping_marker')
          : rawLabel;
        if (!markerCaption || !markerCaption.trim()) return;

        const fragment = this._markerTemplate.content.cloneNode(true);
        const markerEl = fragment.querySelector('.bundle__progress-marker');
        if (!markerEl) return;

        // Every tag now sits BELOW the bar — the old "Most Popular floats
        // above" exception was dropped per the client's request.
        const isLast = idx === visibleRules.length - 1;
        if (total >= rule.minimum) {
          markerEl.classList.add('bundle__progress-marker--reached');
        }

        // Flag the free-shipping marker for the active plan so CSS (glow)
        // and the confetti launcher can target it. `fsRule` already accounts
        // for free_shipping_type vs the subscribe toggle, so this is the 6-pack
        // marker on subscription and the 8-pack one on one-time, etc.
        if (isActiveFsRule) {
          markerEl.classList.add('bundle__progress-marker--free-shipping');
          markerEl.dataset.freeShipping = 'true';
        }

        markerEl.dataset.minimum = String(rule.minimum);
        // Position: mobile spreads markers EVENLY (see _markerPositionPercent);
        // desktop keeps the original minimum/segments placement. Either way the
        // last marker skips the inline `left` — CSS `:last-child` pins it to
        // `right: 0` so the Max Savings pill hugs the bar's end.
        if (!isLast) {
          const leftPct = isMobile
            ? this._markerPositionPercent(idx, visibleRules.length)
            : (rule.minimum / segments) * 100;
          markerEl.style.left = leftPct + '%';
        }

        // The "Free Shipping" caption follows the ACTIVE PLAN, not a fixed
        // per-tier label: fsRule already resolves to whichever tier grants
        // free shipping under the current plan (6-pack on subscription,
        // 8-pack on one-time, etc.), so that's the one tier that should read
        // "Free Shipping" here — regardless of which quantity it happens to
        // be (the mismatched tier never reaches this point — see the early
        // return above).
        // markerCaption is guaranteed non-empty (empty ones skipped above).
        const tag = markerEl.querySelector('.bundle__progress-marker-tag');
        if (tag) tag.textContent = markerCaption;

        progressEl.appendChild(markerEl);
      });
    });

    // Discount tier chips share the bar's visible rules so the two never drift.
    this._renderDiscountTiers(visibleRules, total, fsRule);

    // Celebrate the moment free shipping is unlocked. Fire only on the
    // false→true crossing (guarded by _fsReachedPrev). On the first render
    // _fsReachedPrev is null, so we seed it without animating — that way a
    // page reload with items already past the threshold doesn't pop confetti.
    if (this._fsReachedPrev !== null && freeShippingReached && !this._fsReachedPrev) {
      this._celebrateFreeShipping();
    }
    this._fsReachedPrev = freeShippingReached;
  }

  // ── Discount tier chips ───────────────────────────────────
  // Renders one chip per visible rule ("N CANS / SAVE X%"), filling the chip
  // with the accent colour once its tier is reached (total >= rule.minimum) —
  // the same threshold the matching progress marker uses. `visibleRules` is
  // passed in from _renderProgress so the chips and the bar can never drift,
  // including when the subscribe toggle changes which free-shipping tier shows.
  // `fsRule` is the ACTIVE plan's free-shipping THRESHOLD tier (same one the
  // progress marker glows). Free shipping isn't a one-tier perk — once a plan
  // unlocks it at fsRule.minimum, every higher tier keeps it too, so every
  // chip at or above that quantity gets the "+ Free Shipping" line, not just
  // the exact threshold tier.
  /** @param {Array<any>} visibleRules @param {number} total @param {any} fsRule */
  _renderDiscountTiers(visibleRules, total, fsRule) {
    if (!this._tierTemplate || !this._tierContainerEls.length) return;

    // While Subscribe & Save is on, each chip shows the COMBINED discount: the
    // tier's own value plus the subscription %, so 5/10/15/20 read as
    // 20/25/30/35 at a 15% subscription. The added % drops off when the toggle
    // is unchecked (this method re-runs from _renderAll on every toggle change).
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    const subscriptionBonus = subscribed ? this._subscriptionDiscountPct : 0;

    this._tierContainerEls.forEach((container) => {
      container.replaceChildren();
      visibleRules.forEach((rule) => {
        const fragment = this._tierTemplate.content.cloneNode(true);
        const chip = fragment.querySelector('.bundle__tier');
        if (!chip) return;

        const cansEl = chip.querySelector('[data-bundle-tier-cans]');
        const saveEl = chip.querySelector('[data-bundle-tier-save]');
        const shippingEl = chip.querySelector('[data-bundle-tier-shipping]');
        if (cansEl) cansEl.textContent = this._fillText(this._txt('tier_cans'), { count: rule.minimum });
        // A 0% tier still renders (it's a valid step), but "save 0%" reads as a
        // bug — so blank the save line when the combined percent is 0. With the
        // subscribe toggle on, the subscription bonus still shows.
        if (saveEl) {
          const tierPercent = (Math.round(Number(rule.value) || 0)) + (subscriptionBonus || 0);
          saveEl.textContent = tierPercent > 0 ? this._fillText(this._txt('tier_save'), { percent: tierPercent }) : '';
        }
        if (shippingEl) shippingEl.textContent = (fsRule && (rule.minimum || 0) >= fsRule.minimum) ? this._txt('tier_free_shipping') : '';

        if (total >= rule.minimum) {
          chip.classList.add('bundle__tier--reached');
        }

        container.appendChild(chip);
      });
    });
  }

  // ── Progress fill (piecewise-linear over evenly-spaced markers) ──
  // The markers sit at equal fractions of the bar (1/N, 2/N, … N/N), but the
  // fill still tracks the QUANTITY each tier requires. We build breakpoints
  // mapping (cans → bar position): (0 → 0%) then each tier's minimum → its even
  // position. The fill interpolates linearly between the surrounding two, so it
  // lands exactly on a marker when that tier's minimum is hit and only fills
  // part of the next gap in proportion to the cans still missing.
  /**
   * @param {number} total
   * @param {Array<{ minimum: number }>} visibleRules
   * @param {number} segments
   * @returns {number}
   */
  _computeProgressFill(total, visibleRules, segments) {
    const n = visibleRules.length;
    // No markers to anchor against — fall back to plain segment normalisation.
    if (n === 0) {
      const filledCount = Math.min(Math.max(total, 0), segments);
      return (filledCount / segments) * 100;
    }
    if (total <= 0) return 0;

    // Walk the (cans → position) breakpoints in order: the implicit (0 → 0%)
    // start, then each tier's minimum mapped to its even position (i+1)/N.
    // When `total` falls inside a [prev, curr] span we interpolate; past the
    // last marker the bar is full (its position is 100%).
    let prevQty = 0;
    let prevPos = 0;
    for (let i = 0; i < n; i++) {
      const rule = visibleRules[i];
      const currQty = Number(rule && rule.minimum) || 0;
      const currPos = this._markerPositionPercent(i, n);
      if (total <= currQty) {
        const span = currQty - prevQty;
        const frac = span > 0 ? (total - prevQty) / span : 0;
        return prevPos + frac * (currPos - prevPos);
      }
      prevQty = currQty;
      prevPos = currPos;
    }
    return 100;
  }

  // ── Even marker position (kept in sync between dots + fill) ──
  // Returns the horizontal position (in %) for marker `idx` of `count` markers.
  // Non-last markers sit at PROGRESS_MARKER_LEAD along their 1/N segment, which
  // shifts them all rightward — opening the start→first gap while tightening the
  // gap before the final marker (without letting the tags collide). The last
  // marker is pinned to the bar's right edge (100%) — CSS `:last-child {
  // right: 0 }` actually places it, so JS only needs the matching value here so
  // the fill breakpoints line up with the dots.
  /**
   * @param {number} idx
   * @param {number} count
   * @returns {number}
   */
  _markerPositionPercent(idx, count) {
    if (count <= 0) return 0;
    if (idx >= count - 1) return 100;
    return ((idx + BundleSection.PROGRESS_MARKER_LEAD) / count) * 100;
  }

  // True when the viewport is at/below the 768px mobile breakpoint — gates the
  // evenly-spaced marker layout + piecewise fill so they apply on mobile only.
  // Falls back to false (desktop layout) when matchMedia isn't available.
  /** @returns {boolean} */
  _isMobileProgress() {
    return !!(this._progressMedia && this._progressMedia.matches);
  }

  // ── Free-shipping celebration ─────────────────────────────
  // Launches a confetti burst from the visible free-shipping marker (the
  // sticky bar mirrors the same marker, so we pick whichever copy is on
  // screen). No-op when the user prefers reduced motion.
  _celebrateFreeShipping() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Prefer a marker that's actually rendered on screen (offsetParent !== null
    // skips the collapsed sticky bar / hidden copies). Fall back to the bar's
    // far edge if for some reason none is measurable.
    const markers = this.querySelectorAll('.bundle__progress-marker--free-shipping');
    /** @type {HTMLElement | null} */
    let originEl = null;
    markers.forEach((/** @type {Element} */ m) => {
      const el = /** @type {HTMLElement} */ (m);
      if (!originEl && el.offsetParent !== null) originEl = el;
    });
    if (!originEl && markers.length) originEl = /** @type {HTMLElement} */ (markers[0]);

    let originX = window.innerWidth / 2;
    let originY = window.innerHeight / 2;
    if (originEl) {
      const rect = originEl.getBoundingClientRect();
      if (rect.width || rect.height) {
        originX = rect.left + rect.width / 2;
        originY = rect.top + rect.height / 2;
      }
    }

    launchBundleConfetti(originX, originY);
  }

  // ── Carousel slots ────────────────────────────────────────
  // Slots live inside the Swiper .swiper-wrapper (data-bundle-track).
  // Once selected.length exceeds maxQty, the extras overflow the viewport
  // and become reachable via the navigation arrows that Swiper wires.
  _renderCarousel() {
    // Group once and feed both renderers — duplicate units collapse into a
    // single tile per variant carrying the unit count.
    const groups = this._groupSelected();

    this._renderCarouselInto({
      groups,
      trackEl: this._trackEl,
      slotTemplate: this._slotTemplate,
      slotEmptyTemplate: this._slotEmptyTemplate,
      optionSelector: '.bundle__option',
      imageSelector: '.bundle__option-image',
      circleSelector: '.bundle__option-circle',
      labelSelector: '.bundle__option-label',
      removeSelector: '[data-bundle-slot-remove]',
      stepperValueSelector: '[data-bundle-slot-qty-value]',
      slotAttr: 'data-bundle-slot',
      swiper: this._swiper,
      // Keep the main grid padded to maxQty empty slots so the builder still
      // shows its full row even with only a few flavors grouped in.
      emptyCount: Math.max(0, this._maxQty - groups.length),
    });

    // Sticky bar mirror — same data, smaller circles, no label. The bar shows
    // a fixed grid of 5 slots that fill in as flavors are grouped (empties =
    // the remaining capacity, NOT a constant block appended after the tiles).
    // Once 5+ distinct flavors are grouped, no empty slot is appended — extra
    // flavors are added from the product grid, not from a placeholder here.
    if (this._sticky) {
      const stickyBaseSlots = 5;
      const stickyEmpty = Math.max(0, stickyBaseSlots - groups.length);
      this._renderCarouselInto({
        groups,
        trackEl: this._sticky.trackEl,
        slotTemplate: this._sticky.slotTemplate,
        slotEmptyTemplate: this._sticky.slotEmptyTemplate,
        optionSelector: '.bundle-sticky-cart__option',
        imageSelector: '.bundle-sticky-cart__option-image',
        circleSelector: '.bundle-sticky-cart__option-circle',
        labelSelector: null,
        removeSelector: '[data-bundle-sticky-slot-remove]',
        stepperValueSelector: '[data-bundle-sticky-qty-value]',
        slotAttr: 'data-bundle-sticky-slot',
        swiper: this._sticky.swiper,
        emptyCount: stickyEmpty,
      });
    }
  }

  // Shared slot renderer — used by both the main carousel and the sticky
  // bar. Both consume the same grouped `_selected` data but have different
  // markup (separate templates + class hooks) so we parameterise the
  // selectors instead of duplicating the loop. Each filled tile maps to a
  // variant group; the qty stepper value shows the unit count and the slot
  // carries the variant id + payload as data-attrs so the delegated
  // +/-/× handlers can mutate `_selected` without re-querying the cards.
  /**
   * @param {{
   *   groups: Array<{ item: any, count: number }>,
   *   trackEl: Element | null | undefined,
   *   slotTemplate: Element | null | undefined,
   *   slotEmptyTemplate: Element | null | undefined,
   *   optionSelector: string,
   *   imageSelector: string,
   *   circleSelector: string | null | undefined,
   *   labelSelector: string | null,
   *   removeSelector: string,
   *   stepperValueSelector: string | null | undefined,
   *   slotAttr: string,
   *   swiper: any,
   *   emptyCount: number | undefined,
   * }} opts
   */
  _renderCarouselInto(opts) {
    const { groups, trackEl, optionSelector, imageSelector, circleSelector, labelSelector, removeSelector, stepperValueSelector, slotAttr, swiper, emptyCount } = opts;
    const slotTemplate = /** @type {HTMLTemplateElement | null} */ (opts.slotTemplate);
    const slotEmptyTemplate = /** @type {HTMLTemplateElement | null} */ (opts.slotEmptyTemplate);
    if (!trackEl || !slotTemplate || !slotEmptyTemplate) return;
    const empties = typeof emptyCount === 'number' ? Math.max(0, emptyCount) : 0;

    trackEl.querySelectorAll(`[${slotAttr}]`).forEach((/** @type {Element} */ s) => s.remove());

    groups.forEach((group) => {
      const item = group.item || {};
      const fragment = slotTemplate.content.cloneNode(true);
      const slot = /** @type {HTMLElement | null} */ (/** @type {DocumentFragment} */ (fragment).querySelector(optionSelector));
      if (!slot) return;

      slot.setAttribute(slotAttr, '');
      // Variant id + full payload travel on the tile so the +/-/× controls
      // can add/remove units (and re-add a fresh copy) via delegation.
      slot.dataset.variantId = String(item.variantId || '');
      slot.dataset.price = String(item.price || 0);
      slot.dataset.title = String(item.title || '');
      slot.dataset.image = String(item.image || '');
      slot.dataset.handle = String(item.handle || '');
      slot.dataset.bgColor = String(item.bgColor || '');

      const img = /** @type {HTMLImageElement | null} */ (slot.querySelector(imageSelector));
      if (img) {
        if (item.image) img.src = item.image;
        img.alt = item.title || '';
      }

      // Tint the circle with the product's background-color metafield
      // (same value the PLP card image-shell uses) so each placed flavor
      // keeps its can-art colour in the carousel + sticky bar. The metafield
      // can hold either a bare colour (#hex / rgb()) or a full CSS
      // declaration — wrap bare values so they actually apply.
      if (circleSelector && item.bgColor) {
        const circle = /** @type {HTMLElement | null} */ (slot.querySelector(circleSelector));
        if (circle) {
          const bg = String(item.bgColor).trim();
          circle.style.cssText += bg.indexOf(':') === -1 ? `background-color:${bg};` : bg;
        }
      }

      if (labelSelector) {
        const label = slot.querySelector(labelSelector);
        if (label) label.textContent = item.title || '';
      }

      // Stepper value doubles as the ×N quantity indicator.
      if (stepperValueSelector) {
        const valueEl = slot.querySelector(stepperValueSelector);
        if (valueEl) valueEl.textContent = String(group.count);
      }

      const removeBtn = slot.querySelector(removeSelector);
      if (removeBtn) removeBtn.setAttribute('aria-label', `Remove all ${item.title}`);

      trackEl.appendChild(slot);
    });

    for (let i = 0; i < empties; i++) {
      const fragment = slotEmptyTemplate.content.cloneNode(true);
      const slot = /** @type {HTMLElement | null} */ (/** @type {DocumentFragment} */ (fragment).querySelector(optionSelector));
      if (!slot) continue;
      slot.setAttribute(slotAttr, '');
      trackEl.appendChild(slot);
    }

    if (swiper && typeof swiper.update === 'function') {
      swiper.update();
      // After a remove the active index can point past the last slide,
      // leaving the viewport parked on empty space. Clamp it back.
      const slideCount = swiper.slides ? swiper.slides.length : 0;
      if (slideCount > 0 && typeof swiper.slideTo === 'function' && swiper.activeIndex >= slideCount) {
        swiper.slideTo(slideCount - 1, 0);
      }
    }
  }

  // ── Sidebar stats ─────────────────────────────────────────
  // Visibility of the subscribe savings row, original-strikethrough total
  // and SAVE pill is controlled by CSS (`:has(:checked)` selectors). The
  // numeric values are written here regardless of toggle state so the DOM
  // stays in sync when CSS flips them in.
  _renderSidebar() {
    const pricing = this._computeBundlePricing();
    const hasItems = pricing.total > 0;
    const totalSavingsCents = (pricing.savingsCents || 0) + (pricing.subscriptionSavingsCents || 0);
    const availableSubscriptionPct = this._subscriptionDiscountPct;

    // Empty state (no flavors selected yet):
    //   • Packs Selected → "—"
    //   • Price per Pack → parent product price (unit price has no meaning yet)
    //   • Total          → "—"
    // Savings rows + strikethrough + SAVE pill are gated by CSS attribute
    // selectors so they stay hidden here.
    if (this._packsCountEl) {
      this._packsCountEl.textContent = hasItems ? pricing.total : '—';
    }
    if (this._unitPriceEl) {
      this._unitPriceEl.textContent = hasItems
        ? this._formatMoney(pricing.unitPriceCents)
        : (this._parentPriceCents > 0 ? this._formatMoney(this._parentPriceCents) : '—');
    }
    // Display-only shipping surcharge — added to the shown Total (and its
    // strikethrough) until free shipping is unlocked, then drops to 0. Mirrors
    // the sticky CTA pricing; never touches the cart payload.
    const surchargeCents = this._shippingSurchargeCents(pricing);
    if (this._totalEl) {
      this._totalEl.textContent = hasItems ? this._formatMoney(pricing.finalCents + surchargeCents) : '—';
    }
    if (this._totalOriginalEl) {
      // Show original subtotal (pre-any-discount) as the strikethrough so the
      // savings line matches the difference between the two amounts.
      this._totalOriginalEl.textContent = hasItems && totalSavingsCents > 0
        ? this._formatMoney(pricing.originalCents + surchargeCents)
        : '';
    }
    if (this._tierSavingsEl) {
      // Bundles & Savings — only meaningful when a tier is active.
      this._tierSavingsEl.textContent = pricing.savingsCents > 0
        ? this._fillText(this._txt('savings_format'), { amount: this._formatMoney(pricing.savingsCents), percent: Math.round(pricing.discountPct) })
        : '';
    }
    if (this._subscriptionSavingsEl) {
      this._subscriptionSavingsEl.textContent = hasItems && pricing.subscribed && pricing.subscriptionDiscountPct > 0
        ? this._fillText(this._txt('savings_format'), { amount: this._formatMoney(pricing.subscriptionSavingsCents), percent: pricing.subscriptionDiscountPct || 0 })
        : '';
    }
    if (this._saveBadgeEl) {
      // Combine bundle tier savings + subscribe savings so the pill reflects
      // everything the customer is saving versus the strikethrough total.
      this._saveBadgeEl.textContent = totalSavingsCents > 0
        ? this._fillText(this._txt('save_badge'), { amount: this._formatMoney(totalSavingsCents) })
        : '';
    }

    if (this._subscribeBadgeEl) {
      this._subscribeBadgeEl.textContent = availableSubscriptionPct > 0
        ? `+ ${availableSubscriptionPct}% off every order`
        : 'Subscribe & Save';
    }
    if (this._stickySubscribeLabelEl) {
      this._stickySubscribeLabelEl.textContent = availableSubscriptionPct > 0
        ? `${availableSubscriptionPct}% off Subscribe`
        : 'Subscribe';
    }

    // Sticky CTA pricing — mirrors the sidebar Total row inside the
    // sticky bar button. Empty state: final = $0.00, original hidden
    // (the button itself is also disabled via _renderAddToCart, so the
    // visible $0.00 reads as a placeholder, not a real charge).
    this._renderStickyCtaPricing(pricing, totalSavingsCents);
  }

  /**
   * @param {ReturnType<BundleSection["_computeBundlePricing"]>} pricing
   * @param {number} totalSavingsCents
   */
  _renderStickyCtaPricing(pricing, totalSavingsCents) {
    const sticky = this._sticky;
    if (!sticky) return;
    const hasItems = pricing.total > 0;

    // Visual shipping surcharge: while the active plan hasn't unlocked free
    // shipping, the sticky CTA price shows the bundle total PLUS the flat
    // shipping cost. Display-only — never touches pricing.finalCents nor the
    // /cart/add payload — and drops to 0 once free shipping is reached. Same
    // helper drives the sidebar Total so both stay in sync.
    const surchargeCents = this._shippingSurchargeCents(pricing);

    if (sticky.ctaFinalEl) {
      sticky.ctaFinalEl.textContent = hasItems
        ? this._formatMoney(pricing.finalCents + surchargeCents)
        : this._formatMoney(0);
    }
    if (sticky.ctaOriginalEl) {
      const showOriginal = hasItems && totalSavingsCents > 0;
      if (showOriginal) {
        // Mirror the surcharge on the strikethrough so it stays above the
        // final amount and the savings delta between them is preserved.
        sticky.ctaOriginalEl.textContent = this._formatMoney(pricing.originalCents + surchargeCents);
        sticky.ctaOriginalEl.removeAttribute('hidden');
      } else {
        sticky.ctaOriginalEl.textContent = '';
        sticky.ctaOriginalEl.setAttribute('hidden', '');
      }
    }
  }

  _renderAddToCart() {
    const ready = this._selected.length >= this._minQty && this._selected.length > 0;
    const disabled = !ready || !!this._submitting;
    if (this._addToCartBtn) {
      this._addToCartBtn.dataset.ready = ready ? 'true' : 'false';
      this._addToCartBtn.disabled = disabled;
    }
    if (this._sticky && this._sticky.addToCartBtn) {
      this._sticky.addToCartBtn.dataset.ready = ready ? 'true' : 'false';
      this._sticky.addToCartBtn.disabled = disabled;
    }
    // Keep the sticky subscribe toggle visually in sync with the sidebar
    // one — bidirectional change handlers in _bindEvents() handle the
    // user-initiated path; this catches programmatic toggles too.
    const stickyToggle = this._sticky ? this._sticky.subscribeToggle : null;
    const mainToggle = this._subscribeToggleEl;
    if (stickyToggle && mainToggle && stickyToggle.checked !== mainToggle.checked) {
      stickyToggle.checked = mainToggle.checked;
    }
  }

  // ── Pricing ───────────────────────────────────────────────
  _findActiveRule(total) {
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    const eligible = this._discountRules.filter((r) => {
      // Plan-hidden tiers (see _isRuleVisibleForPlan) must not apply their
      // discount either, so the price matches the visible chips/bar.
      if (!this._isRuleVisibleForPlan(r, subscribed)) return false;
      const minimum = Number(r?.minimum || 0);
      const rawMaximum = r?.maximum;
      const maximum = rawMaximum === null || rawMaximum === undefined || rawMaximum === ''
        ? null
        : Number(rawMaximum);
      const type = String(r?.type || '').toLowerCase().trim();

      if (!(minimum > 0) || total < minimum) return false;

      if (type === 'fixed quantity' || type === 'fixed_quantity' || type === 'fixed') {
        return total === minimum;
      }

      if (Number.isFinite(maximum) && maximum > 0) {
        return total <= maximum;
      }

      return true;
    });
    if (eligible.length === 0) return null;
    return eligible.reduce((max, r) => ((r.minimum || 0) > (max.minimum || 0) ? r : max), eligible[0]);
  }

  _computeBundlePricing() {
    const total = this._selected.length;
    let originalCents = 0;
    this._selected.forEach((item) => {
      const unitPrice = Number(item?.price || 0);
      if (unitPrice > 0) originalCents += unitPrice;
    });

    const activeRule = this._findActiveRule(total);
    const pct = activeRule ? Number(activeRule.value) || 0 : 0;
    let discountedCents = 0;
    this._selected.forEach((item) => {
      const unitPrice = Number(item?.price || 0);
      if (unitPrice <= 0) return;
      const itemDiscounted = Math.round(unitPrice * (100 - pct) / 100);
      discountedCents += Math.max(itemDiscounted, 0);
    });

    const savingsCents = Math.max(originalCents - discountedCents, 0);
    const unitPriceCents = total > 0 ? Math.round(discountedCents / total) : 0;

    // Subscribe & Save applies an extra discount on top of the tier total.
    // The strikethrough "old price" shown in the sidebar is the bundle
    // tier price (discountedCents) — not the raw originalCents — so the
    // savings line matches what the user is actually paying less.
    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    const subscriptionDiscountPct = subscribed ? this._subscriptionDiscountPct : 0;
    let subscriptionSavingsCents = 0;
    if (subscribed && subscriptionDiscountPct > 0) {
      this._selected.forEach((item) => {
        const unitPrice = Number(item?.price || 0);
        if (unitPrice <= 0) return;
        // Subscription discount adds to the quantity-tier discount (simple
        // sum on the original price), it does not compound on top of the
        // already-discounted tier price. e.g. tier 5% + subscription 15%
        // must total 20% off, not 1-(0.95*0.85)=19.25%.
        // Shopify discount allocations on subscription lines can effectively
        // behave like a floor in cents; using floor here avoids over-discount
        // drift versus the cart drawer totals at higher bundle quantities.
        const itemSubscriptionSavings = Math.floor(Math.max(unitPrice, 0) * subscriptionDiscountPct / 100);
        subscriptionSavingsCents += Math.max(itemSubscriptionSavings, 0);
      });
    }
    const finalCents = Math.max(discountedCents - subscriptionSavingsCents, 0);

    return {
      total,
      originalCents,
      discountedCents,
      savingsCents,
      unitPriceCents,
      activeRule,
      discountPct: pct,
      subscribed,
      subscriptionDiscountPct,
      subscriptionSavingsCents,
      finalCents,
    };
  }

  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this._moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  // ── Cart submission ───────────────────────────────────────
  // Mirrors bb-panel._doAddToCart from assets/bundle-builder.js so the
  // cart drawer refresh, header cart count and cart:update dispatch all
  // behave identically to the existing bundle-builder section.
  async _addBundleToCart() {
    if (this._submitting) return;
    if (!this._addToCartBtn || this._addToCartBtn.dataset.ready !== 'true') return;
    if (this._selected.length < this._minQty || this._selected.length === 0) return;

    // Group selected units by variantId so the cart line uses { id, quantity }
    // instead of one POST entry per unit.
    const grouped = new Map();
    this._selected.forEach((item) => {
      const id = item.variantId;
      if (!id) return;
      const existing = grouped.get(id) || { id, quantity: 0 };
      existing.quantity += 1;
      grouped.set(id, existing);
    });

    const subscribed = !!(this._subscribeToggleEl && this._subscribeToggleEl.checked);
    const activeBundleProperties = await this._getActiveBundleProperties({ subscribed });
    const bundleId = activeBundleProperties?._bb_bundle_id || this._generateBundleId();
    const total = this._selected.length;
    /** @type {Record<string, string>} */
    const properties = activeBundleProperties || {
      _bb_bundle_id: bundleId,
      _bb_bundle_size: String(total),
      _bb_bundle_min_qty: String(this._minQty),
      _bb_source: 'bundle-section',
    };
    properties._bb_bundle_id = bundleId;
    if (!properties._bb_bundle_size) properties._bb_bundle_size = String(total);
    if (!properties._bb_bundle_min_qty) properties._bb_bundle_min_qty = String(this._minQty);
    if (!properties._bb_source) properties._bb_source = 'bundle-section';
    if (this._parentVariantId && !properties._bb_parent_variant_id) properties._bb_parent_variant_id = this._parentVariantId;
    if (this._parentTitle && !properties._bb_parent_title) properties._bb_parent_title = this._parentTitle;
    if (this._parentImage && !properties._bb_parent_image) properties._bb_parent_image = this._parentImage;

    // Attach the selected selling plan when Subscribe is ON so Shopify
    // registers each line as a subscription order.
    // Selling plan IDs are per-variant in Shopify, so we build a map from
    // variantId → sellingPlanId using the data stored on each rendered card.
    if (subscribed) properties._bb_subscribed = 'true';

    const cardPlanMap = new Map();
    if (subscribed) {
      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        const el = /** @type {HTMLElement} */ (card);
        const vid = el.dataset.bundleCardVariantId || '';
        const planId = el.dataset.bundleCardSellingPlanId || '';
        if (vid && planId) cardPlanMap.set(vid, planId);
      });
    }

    const items = Array.from(grouped.values()).map((entry) => {
      /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
      const item = {
        id: parseInt(String(entry.id), 10),
        quantity: entry.quantity,
        properties: { ...properties },
      };
      if (subscribed) {
        const planId = parseInt(cardPlanMap.get(String(entry.id)) || '0', 10);
        if (planId) item.selling_plan = planId;
      }
      return item;
    });
    if (!items.length) return;

    const btn = this._addToCartBtn;
    const stickyBtn = this._sticky ? this._sticky.addToCartBtn : null;
    // The sticky button has a structured inner DOM (label span + pricing
    // wrapper). Writing into the label keeps the pricing markup alive so
    // _renderStickyCtaPricing keeps controlling the price spans after the
    // transient state ends. If the label span is missing (older markup),
    // fall back to the button itself.
    const stickyLabelEl = this._sticky ? (this._sticky.ctaLabelEl || stickyBtn) : null;
    const originalText = btn.textContent || '';
    const stickyOriginalLabel = stickyLabelEl ? (stickyLabelEl.textContent || '') : '';
    /** @param {string} text */
    const setCtaText = (text) => {
      btn.textContent = text;
      if (stickyLabelEl) stickyLabelEl.textContent = text;
    };
    /** @param {boolean} loading */
    const setCtaLoading = (loading) => {
      btn.dataset.loading = loading ? 'true' : 'false';
      if (stickyBtn) stickyBtn.dataset.loading = loading ? 'true' : 'false';
    };
    this._submitting = true;
    setCtaLoading(true);
    setCtaText('ADDING…');

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || 'Cart error');
      }

      // Refresh the cart drawer's section HTML in-place. The drawer
      // contains <cart-items-component data-section-id="…">, so we
      // request /cart?sections=<id> and swap the rendered HTML.
      const cartItemsEl = /** @type {HTMLElement|null} */ (
        document.querySelector('cart-items-component[data-section-id]')
      );
      const sectionId = cartItemsEl ? cartItemsEl.dataset.sectionId : null;
      const cartUrl = sectionId ? `/cart?sections=${sectionId}` : '/cart.js';
      const cartData = await fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then((r) => r.json());

      if (sectionId && cartData[sectionId] && cartItemsEl) {
        const parser = new DOMParser();
        const freshDoc = parser.parseFromString(cartData[sectionId], 'text/html');
        const freshEl = freshDoc.querySelector('cart-items-component');
        if (freshEl) cartItemsEl.innerHTML = freshEl.innerHTML;
      }

      // Theme-wide notification — header-bravo updates the cart count
      // listening to this exact event, same shape bb-panel uses.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: 'bundle-section',
            data: { itemCount: cartData.item_count },
          },
        })
      );

      // Open the cart drawer
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

      setCtaText('✓ ADDED TO CART!');

      setTimeout(() => {
        setCtaLoading(false);
        btn.textContent = originalText;
        if (stickyLabelEl) stickyLabelEl.textContent = stickyOriginalLabel;
        this._submitting = false;
        // Reset bundle selection after the success state finishes.
        this._selected = [];
        this._renderAll();
      }, 2000);
    } catch (error) {
      console.error('[bundle-section] add to cart failed', error);
      setCtaText('ERROR — TRY AGAIN');
      setCtaLoading(false);
      this._submitting = false;
      setTimeout(() => {
        btn.textContent = originalText;
        if (stickyLabelEl) stickyLabelEl.textContent = stickyOriginalLabel;
        this._renderAddToCart();
      }, 2000);
    }
  }

  _generateBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `bb-${window.crypto.randomUUID()}`;
    }
    return `bb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Reuses active bundle metadata when a matching bundle-section group
   * is already in cart. This keeps repeated adds in the same drawer group.
   * @param {{ subscribed: boolean }} config
  * @returns {Promise<Record<string, string> | null>}
   */
  async _getActiveBundleProperties(config) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const targetParentVariantId = String(this._parentVariantId || '').trim();
      const targetSubscribed = !!config.subscribed;

      const bundleCandidates = items.filter((item) => {
        const properties = item?.properties || {};
        const bundleId = String(properties._bb_bundle_id || '').trim();
        if (!bundleId) return false;

        const source = String(properties._bb_source || '').trim();
        if (source !== 'bundle-section') return false;

        const parentVariantId = String(properties._bb_parent_variant_id || '').trim();
        if (targetParentVariantId && parentVariantId !== targetParentVariantId) return false;

        const itemSubscribed = String(properties._bb_subscribed || '').toLowerCase() === 'true';
        if (itemSubscribed !== targetSubscribed) return false;

        return true;
      });

      if (!bundleCandidates.length) {
        return null;
      }

      const bundleIds = new Set(
        bundleCandidates
          .map((item) => String(item?.properties?._bb_bundle_id || '').trim())
          .filter(Boolean)
      );

      if (bundleIds.size !== 1) {
        return null;
      }

      const activeBundleProperties = bundleCandidates[0]?.properties || {};

      const normalizedBundleProperties = Object.entries(activeBundleProperties).reduce((accumulator, [key, value]) => {
        if (!key.startsWith('_bb_')) {
          return accumulator;
        }

        if (value === null || value === undefined) {
          return accumulator;
        }

        accumulator[key] = String(value);
        return accumulator;
      }, {});

      if (!Object.keys(normalizedBundleProperties).length) {
        return null;
      }

      return normalizedBundleProperties;
    } catch (error) {
      console.error('[bundle-section] Failed to resolve active bundle metadata', error);
      return null;
    }
  }
}

/* ============================================================
   Lightweight confetti — self-contained, no dependencies.
   A single full-viewport <canvas> is reused across launches and
   removed once every particle has settled. Driven by rAF; particles
   get gravity + drag + spin and fade out near the end of their life.
   ============================================================ */

/** @type {{ canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, particles: any[], raf: number } | null} */
let _bundleConfettiState = null;

/**
 * @param {number} originX  viewport X to burst from
 * @param {number} originY  viewport Y to burst from
 */
function launchBundleConfetti(originX, originY) {
  if (typeof document === 'undefined') return;

  // Brand-leaning palette: accent cyans + a couple of festive highlights.
  const colors = ['#09797C', '#23DEDB', '#55F3EA', '#FAFAFA', '#FFD166', '#FF7E6B'];
  const count = 130;

  if (!_bundleConfettiState) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:99999;';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return;
    }
    _bundleConfettiState = { canvas, ctx, particles: [], raf: 0 };
  }

  const state = _bundleConfettiState;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  state.canvas.width = window.innerWidth * dpr;
  state.canvas.height = window.innerHeight * dpr;
  state.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  for (let i = 0; i < count; i++) {
    // Burst fans mostly upward/outward; randomised so each launch differs.
    const angle = Math.PI + (Math.random() - 0.5) * Math.PI * 1.2; // up-ish cone
    const speed = 6 + Math.random() * 8;
    state.particles.push({
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - Math.random() * 4,
      size: 5 + Math.random() * 6,
      color: colors[(Math.random() * colors.length) | 0],
      rotation: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.3,
      circle: Math.random() < 0.35,
      life: 0,
      maxLife: 90 + Math.random() * 50,
    });
  }

  if (state.raf) return; // already animating — new particles join the loop

  const gravity = 0.22;
  const drag = 0.985;

  const tick = () => {
    const { ctx, canvas, particles } = state;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life++;
      p.vx *= drag;
      p.vy = p.vy * drag + gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;

      const lifeRatio = p.life / p.maxLife;
      if (lifeRatio >= 1 || p.y - p.size > window.innerHeight) {
        particles.splice(i, 1);
        continue;
      }

      ctx.save();
      ctx.globalAlpha = lifeRatio > 0.7 ? 1 - (lifeRatio - 0.7) / 0.3 : 1;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      if (p.circle) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      }
      ctx.restore();
    }

    if (particles.length) {
      state.raf = requestAnimationFrame(tick);
    } else {
      // All settled — tear down so we don't keep a canvas on the page.
      cancelAnimationFrame(state.raf);
      state.canvas.remove();
      _bundleConfettiState = null;
    }
  };

  state.raf = requestAnimationFrame(tick);
}

if (!customElements.get('bundle-section')) {
  customElements.define('bundle-section', BundleSection);
}
